import EmblaCarousel from 'embla-carousel';

import './view.css';

/**
 * Initialize Embla Carousel
 * Structure is already in HTML from save.tsx
 *
 * Dynamic layout behaviour based on slide count + slidesPerView:
 * - ≤slidesPerView (desktop >979px):  static cards, no Embla (cards-fit)
 * - >slidesPerView (desktop):         Embla with/without loop + arrows
 * - tablet (≤979px):                  Embla, 2.5 slides visible (contained)
 * - mobile (≤768px):                  Embla, 1 slide + 10% peek (contained)
 *
 * Layout (contained / edge-bleed) is always respected as set by the user.
 */
function initCarousel(viewportElement: HTMLElement): void {
  const emblaViewport = viewportElement.querySelector<HTMLElement>('.embla');
  if (!emblaViewport) return;

  const carouselBlock = viewportElement.closest<HTMLElement>('.wp-block-interact-carousel');
  if (!carouselBlock) return;

  const prevBtn = carouselBlock.querySelector<HTMLButtonElement>('[data-carousel-prev]');
  const nextBtn = carouselBlock.querySelector<HTMLButtonElement>('[data-carousel-next]');
  prevBtn?.setAttribute('aria-label', 'Previous slide');
  nextBtn?.setAttribute('aria-label', 'Next slide');

  const container = emblaViewport.querySelector<HTMLElement>('.embla__container');
  if (!container) return;

  const slideCount = container.children.length;
  const enableLoop = carouselBlock.getAttribute('data-enable-loop') !== 'false';
  const layout = carouselBlock.getAttribute('data-layout') || 'contained';
  const slidesPerView = parseInt(carouselBlock.getAttribute('data-slides-per-view') || '3', 10);

  // Cards-fit: contained layout on desktop when slides fit the viewport.
  if (layout === 'contained' && slideCount <= slidesPerView && window.innerWidth > 979) {
    carouselBlock.setAttribute('data-cards-fit', 'true');
    if (prevBtn) prevBtn.style.display = 'none';
    if (nextBtn) nextBtn.style.display = 'none';
    return;
  }

  // Edge-bleed non-loop: if all slides fit viewport, skip Embla (no scroll needed).
  if (layout === 'edge-bleed' && !enableLoop && window.innerWidth > 979) {
    const viewportWidth = emblaViewport.getBoundingClientRect().width;
    const slideMaxWidth =
      parseFloat(getComputedStyle(carouselBlock).getPropertyValue('--slide-max-width')) || 460;
    const gap = parseFloat(getComputedStyle(carouselBlock).getPropertyValue('--slides-gap')) || 16;
    const totalWidth = slideCount * slideMaxWidth + (slideCount - 1) * gap;
    if (totalWidth <= viewportWidth) {
      if (prevBtn) prevBtn.style.display = 'none';
      if (nextBtn) nextBtn.style.display = 'none';
      // Equal height via flex stretch (no Embla to handle it).
      // Slides have height:100% from CSS Module which blocks stretch — reset to auto.
      container.style.alignItems = 'stretch';
      container.querySelectorAll<HTMLElement>('.embla__slide').forEach((slide) => {
        slide.style.height = 'auto';
      });
      return;
    }
  }

  // Embla loop requires enough slides to fill ~2× the viewport.
  // Clone original slides when there aren't enough for seamless looping.
  if (enableLoop) {
    const originalSlides = Array.from(container.children) as HTMLElement[];
    const viewportWidth = emblaViewport.getBoundingClientRect().width;
    const firstSlide = originalSlides[0];

    if (firstSlide && viewportWidth > 0) {
      const slideWidth = firstSlide.getBoundingClientRect().width;
      const gap = parseInt(
        getComputedStyle(carouselBlock).getPropertyValue('--slides-gap') || '16',
        10,
      );
      const needed = viewportWidth * 2;
      let total = originalSlides.length * (slideWidth + gap);

      let rounds = 0;
      while (total < needed && rounds < 10) {
        rounds++;
        originalSlides.forEach((slide) => {
          const clone = slide.cloneNode(true) as HTMLElement;
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('inert', '');
          container.appendChild(clone);
          total += slideWidth + gap;
        });
      }
    }
  }

  // Init Embla carousel
  // Non-loop: 'trimSnaps' removes duplicate snap points at the end so every
  // prev/next click produces a visible scroll (keepSnaps caused double-click at boundaries).
  // Loop: omit containScroll (Embla default) — loop ignores it anyway.
  const emblaApi = EmblaCarousel(emblaViewport, {
    loop: enableLoop,
    align: 'start',
    slidesToScroll: 1,
    skipSnaps: false,
    ...(enableLoop ? {} : { containScroll: 'trimSnaps' }),
  });

  let resizeTimeout: number | undefined;
  let isDestroyed = false;

  // Set equal height for all slides based on tallest slide.
  // Runs synchronously so Embla sees correct dimensions on reInit.
  const setEqualHeight = () => {
    if (isDestroyed) return;

    const slides = emblaViewport.querySelectorAll<HTMLElement>('.embla__slide');
    if (slides.length === 0) return;

    // Reset heights to measure natural height
    slides.forEach((slide) => {
      slide.style.height = 'auto';
    });

    // Find max height
    let maxHeight = 0;
    slides.forEach((slide) => {
      const height = slide.offsetHeight;
      if (height > maxHeight) {
        maxHeight = height;
      }
    });

    // Apply max height to all slides
    if (maxHeight > 0) {
      slides.forEach((slide) => {
        slide.style.height = `${maxHeight}px`;
      });
    }
  };

  // Debounced resize handler (specific to this carousel instance)
  const handleResize = () => {
    if (resizeTimeout !== undefined) {
      clearTimeout(resizeTimeout);
    }
    resizeTimeout = window.setTimeout(refreshEmbla, 200);
  };

  // Use ResizeObserver for better performance (falls back to resize event)
  let resizeObserver: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      handleResize();
    });
    resizeObserver.observe(emblaViewport);
  } else {
    // Fallback to window resize
    window.addEventListener('resize', handleResize, { passive: true });
  }

  // Set equal height on init (measurements will be corrected by the deferred reInit below).
  // IMPORTANT: do NOT listen for 'reInit' — setEqualHeight changes heights AFTER Embla
  // recalculates, leaving measurements stale. Instead, call setEqualHeight BEFORE every reInit.
  emblaApi.on('init', setEqualHeight);

  // Helper: recalculate heights then tell Embla to remeasure.
  const refreshEmbla = () => {
    if (isDestroyed) return;
    setEqualHeight();
    emblaApi.reInit();
  };

  // Ensure images are loaded before calculating height
  const images = emblaViewport.querySelectorAll('img');
  if (images.length > 0) {
    let loadedCount = 0;
    const totalImages = images.length;

    const checkAllLoaded = () => {
      loadedCount++;
      if (loadedCount === totalImages) {
        requestAnimationFrame(refreshEmbla);
      }
    };

    images.forEach((img) => {
      if (img.complete) {
        checkAllLoaded();
      } else {
        img.addEventListener('load', checkAllLoaded, { once: true });
        img.addEventListener('error', checkAllLoaded, { once: true });
      }
    });
  } else {
    setEqualHeight();
  }

  // Cleanup function
  const cleanup = () => {
    isDestroyed = true;
    if (resizeTimeout !== undefined) {
      clearTimeout(resizeTimeout);
    }
    if (resizeObserver) {
      resizeObserver.disconnect();
    } else {
      window.removeEventListener('resize', handleResize);
    }
  };

  // Clean up on destroy
  emblaApi.on('destroy', cleanup);

  // Reinit after first paint so Embla recalculates with final CSS widths.
  // Double-rAF ensures browser has painted the heights from init before we remeasure.
  requestAnimationFrame(() => {
    requestAnimationFrame(refreshEmbla);
  });

  // Reveal arrows now that we know this carousel needs scrolling.
  // They start hidden via CSS to prevent a flash on carousels that skip Embla.
  const controlsContainer = carouselBlock.querySelector<HTMLElement>(
    '.wp-block-interact-carousel-controls',
  );
  if (controlsContainer) {
    controlsContainer.style.visibility = 'visible';
  }

  // Disable buttons when at start/end (non-loop only).
  // Only check on 'select' (snap changed) and 'settle' (animation done) —
  // NOT on 'scroll' or 'reInit' where canScrollPrev/Next may return stale values.
  const updateButtons = () => {
    if (enableLoop) return;
    if (prevBtn) prevBtn.disabled = !emblaApi.canScrollPrev();
    if (nextBtn) nextBtn.disabled = !emblaApi.canScrollNext();
  };

  emblaApi.on('select', updateButtons);
  emblaApi.on('settle', updateButtons);

  // Defer initial button state until after the double-rAF reInit settles
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(updateButtons);
    });
  });

  // Button click handlers
  if (prevBtn) {
    prevBtn.addEventListener('click', () => emblaApi.scrollPrev(), false);
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', () => emblaApi.scrollNext(), false);
  }

  // Pagination dots (mobile only — hidden on desktop via CSS)
  const dotsContainer = document.createElement('div');
  dotsContainer.className = 'carousel-dots';
  dotsContainer.setAttribute('role', 'tablist');
  dotsContainer.setAttribute('aria-label', 'Carousel pagination');
  viewportElement.after(dotsContainer);

  const buildDots = () => {
    const snaps = emblaApi.scrollSnapList();
    dotsContainer.innerHTML = '';
    snaps.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.className = 'carousel-dot';
      dot.type = 'button';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => emblaApi.scrollTo(i), false);
      dotsContainer.appendChild(dot);
    });
    updateDots();
  };

  const updateDots = () => {
    const selected = emblaApi.selectedScrollSnap();
    const dots = dotsContainer.querySelectorAll<HTMLElement>('.carousel-dot');
    dots.forEach((dot, i) => {
      const isActive = i === selected;
      dot.classList.toggle('carousel-dot--active', isActive);
      dot.setAttribute('aria-selected', String(isActive));
    });
  };

  emblaApi.on('select', updateDots);
  emblaApi.on('reInit', buildDots);
  buildDots();
}

/**
 * Initialize all carousels on page load
 */
document.addEventListener('DOMContentLoaded', () => {
  const viewports = document.querySelectorAll<HTMLElement>('[data-carousel-viewport]');

  viewports.forEach((viewport) => {
    initCarousel(viewport);
  });
});
